import React, { useEffect, useMemo } from 'react';
import { EFabricationRoutes } from '../fabrication.routes';
import { FabricationContainer } from '../styles';
import {
  DetailingProjectBlock,
  ObservationContainer,
  ButtonContainer
} from './styles';
import DetailingProjectContainer from './DetailingProjectContainer';
import {
  getDefaultFabricationProjectItem,
  EFabricationOrderSteps,
  EProductType,
  IDetailingProjectState,
  IConstructionIdentificationState
} from '../../../../store/ducks/fabrication-purchase-order/types';
import { IFabricationProjectsState } from '../../../../store/ducks/fabrication-projects/types';
import { isAllFieldsValidInDetailingProject } from '../fabricationOrderFieldsValidations';
import {
  EVehicleTypes,
  IFabricationContract
} from '../../../../store/ducks/fabrication-contracts/types';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { loadProjects } from '../../../../store/ducks/fabrication-projects/actions';
import VehicleType from './VehicleType';
import { Button, Title, TextArea } from '@gerdau/hefesto';
import { Chips } from '@gerdau/hefesto-mais-web';
import { TChipArray } from '@gerdau/hefesto-mais-web/Chips/Chips.types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

interface IConstructionIdentificationProps {
  fabricationProjectsState: IFabricationProjectsState;
  detailingProjectState: IDetailingProjectState;
  setDetailingProjectState(detailingProjectState: IDetailingProjectState): void;
  contractNumber: string;
  location: string;
  goToFabricationRoute(route: EFabricationRoutes): void;
  onInitFabricationRoute(fabricationOrderStep: EFabricationOrderSteps): void;
  constructionIdentificationState?: IConstructionIdentificationState;
}

const DetailingProject: React.FC<IConstructionIdentificationProps> = ({
  fabricationProjectsState,
  detailingProjectState,
  setDetailingProjectState,
  contractNumber,
  location,
  goToFabricationRoute,
  onInitFabricationRoute,
  constructionIdentificationState
}) => {
  const dispatch = useDispatch();

  const contractsState = useSelector(
    (appState: ApplicationState) => appState.fabricationContracts
  );

  const productTypeOptions: EProductType[] = useMemo(() => {
    return Object.values(EProductType).filter(
      (type: EProductType) => type !== EProductType.VERGALHAO_CORTADO_DOBRADO
    );
  }, [EProductType]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateStateField = (field: string, newValue: any) => {
    setDetailingProjectState({
      ...detailingProjectState,
      [field]: newValue
    });
  };

  const initVehicleType = () => {
    if (
      contractsState.contracts === undefined ||
      detailingProjectState.vehiclesTypes !== undefined
    ) {
      return;
    }

    const contract = contractsState.contracts.find(
      (contr) => contr.contractNumber === contractNumber
    ) as IFabricationContract;
    setDetailingProjectState({
      ...detailingProjectState,
      vehiclesTypes: contract.logistic?.vehicleType || []
    });
  };

  useEffect(() => {
    onInitFabricationRoute(EFabricationOrderSteps.DETAILING_PROJECT);
    initVehicleType();
    // eslint-disable-next-line
  }, []);

  const checkIfAnySelectedProjectDoesNotExist = () => {
    if (
      !fabricationProjectsState.loading &&
      detailingProjectState.itens.some(
        (item) =>
          !!item.projectId &&
          !fabricationProjectsState.projects?.find(
            (p) => p.id === item.projectId
          )
      )
    ) {
      const newProjectItens = detailingProjectState.itens.filter((item) =>
        fabricationProjectsState.projects?.find((p) => p.id === item.projectId)
      );
      setDetailingProjectState({
        ...detailingProjectState,
        itens:
          newProjectItens.length > 0
            ? newProjectItens
            : [getDefaultFabricationProjectItem()]
      });
    }
  };

  const loadFabricationProjectsIfNeed = () => {
    if (
      contractNumber !== undefined &&
      ((!fabricationProjectsState.projects &&
        !fabricationProjectsState.loading &&
        !fabricationProjectsState.error) ||
        fabricationProjectsState.contractNumber !== contractNumber)
    ) {
      dispatch(loadProjects(contractNumber));
      return;
    }

    checkIfAnySelectedProjectDoesNotExist();
  };

  useEffect(() => {
    if (
      !fabricationProjectsState.loading ||
      !fabricationProjectsState.projects
    ) {
      loadFabricationProjectsIfNeed();
    }
  });

  const onSelectProductType = (newValue: EProductType) => {
    if (newValue === EProductType.ESTACA_ARMADA) {
      setDetailingProjectState({
        ...detailingProjectState,
        productType: newValue,
        vehiclesTypes: [EVehicleTypes.CARRETA]
      });
    } else {
      updateStateField('productType', newValue);
    }
  };

  const canShowToNextStep = () => {
    return isAllFieldsValidInDetailingProject(
      detailingProjectState,
      fabricationProjectsState
    );
  };

  const canShowEstacaArmada = (location: string) => {
    return (
      location === '2401' ||
      location === '2408' ||
      location === '2414' ||
      location === '2405' ||
      location === '2412'
    );
  };

  const getChips = (): TChipArray[] => {
    const chips: TChipArray[] = [];

    productTypeOptions.forEach((product) => {
      if (product === EProductType.ESTACA_ARMADA) {
        canShowEstacaArmada(location) &&
          chips.push({
            id: productTypeOptions.indexOf(product).toString(),
            label: product.toString()
          });
        return;
      }
      chips.push({
        id: productTypeOptions.indexOf(product).toString(),
        label: product.toString()
      });
    });

    return chips;
  };

  const history = useHistory();

  const { screenName = '' } = getTaggingInformationScreen(
    history.location.pathname,
    true
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName,
        event: 'page_view'
      })
    );
  }, []);

  const previousStep = () => {
    dispatch(
      sendTagging({
        screenName: 'LG:Comprar:CorteEDobra:DetalhamentoProjeto',
        itemName: 'button:voltar',
        itemDescription: 'comprar_corteEDobra_detalhamentoProjeto_voltar',
        orderCliente: constructionIdentificationState?.orderNumber,
        contratoObra: contractNumber,
        filterInfo: `#cttObra:${contractNumber}#orderCliente:${
          constructionIdentificationState?.orderNumber
        }#dtDesejada:${
          constructionIdentificationState?.desiredDate
            ? moment(constructionIdentificationState?.desiredDate).format(
                'YYYY/MM/DD'
              )
            : null
        }`,
        event: 'click'
      })
    );
    goToFabricationRoute(EFabricationRoutes.CONSTRUCTION_IDENTIFICATION);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextStep = () => {
    dispatch(
      sendTagging({
        screenName: 'LG:Comprar:CorteEDobra:DetalhamentoProjeto',
        itemName: 'button:avancar_para_itens_extras',
        itemDescription:
          'comprar_corteEDobra_detalhamentoProjeto_avancarParaItensExtras',
        orderCliente: constructionIdentificationState?.orderNumber,
        contratoObra: contractNumber,
        filterInfo: `#cttObra:${contractNumber}#orderCliente:${
          constructionIdentificationState?.orderNumber
        }#dtDesejada:${
          constructionIdentificationState?.desiredDate
            ? moment(constructionIdentificationState?.desiredDate).format(
                'YYYY/MM/DD'
              )
            : null
        }`,
        event: 'click'
      })
    );
    goToFabricationRoute(EFabricationRoutes.EXTRA_ITENS);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <FabricationContainer isFacelift={true}>
      <DetailingProjectBlock>
        <Title level={2}>Tipo de produto</Title>
        <span className="subtitle">
          Selecione o tipo de produto que deseja comprar
        </span>
        <Chips
          selectedChipIds={
            getChips().length > 1
              ? [
                  productTypeOptions
                    .indexOf(detailingProjectState.productType)
                    .toString()
                ]
              : ['0']
          }
          type={'choice'}
          chips={getChips()}
          handleSelect={(e: string[]) => {
            if (e.length) {
              const [option] = e;
              onSelectProductType(productTypeOptions[Number(option)]);
            }
          }}
        />
      </DetailingProjectBlock>

      <DetailingProjectBlock>
        <Title level={2}>Informações de entrega</Title>

        <VehicleType
          vehiclesTypes={detailingProjectState.vehiclesTypes || []}
          onSelectVehicleType={(checkedValue) =>
            updateStateField('vehiclesTypes', checkedValue)
          }
          productType={detailingProjectState.productType}
        />
      </DetailingProjectBlock>

      <DetailingProjectContainer
        contractNumber={contractNumber}
        detailingProjectState={detailingProjectState}
        setDetailingProjectState={setDetailingProjectState}
        fabricationProjectsState={fabricationProjectsState}
      />

      <ObservationContainer>
        <Title children={'Observações gerais'} level={2} />

        <TextArea
          label={''}
          name={'Observações gerais'}
          rows={6}
          resizable={false}
          value={detailingProjectState.observation}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            updateStateField('observation', e.target.value)
          }
          maxLength={500}
          supportMessage={
            detailingProjectState.observation.length == 500
              ? 'Deve ter no máximo 500 caracteres'
              : ''
          }
        />
      </ObservationContainer>

      <ButtonContainer>
        <Button
          type="button"
          variant="secondary"
          labelPosition="center"
          name="cancelAndReturnButton"
          onClick={() => previousStep()}
        >
          Voltar
        </Button>
        <Button
          name="goToExtraItensButton"
          onClick={() => nextStep()}
          variant="primary"
          disabled={!canShowToNextStep()}
        >
          Avançar para itens extras
        </Button>
      </ButtonContainer>
    </FabricationContainer>
  );
};

export default DetailingProject;
